/**
 * Shared auth context type and hooks for the embed iframe (helpdesk widget).
 */

import { createContext, useContext } from 'react';

export interface GoogleUser {
  id: string;
  email: string;
  name: string;
  picture?: string;
}

export interface AuthState {
  pageUrl: string;
  pageTitle: string;
  userName: string | null;
  userEmail: string | null;
  /** Google-authenticated user info (when Google session is active) */
  googleUser: GoogleUser | null;
  /** True while the initial Google session cookie check is in-flight. */
  googleSessionLoading: boolean;
  authError: string | null;
  /** User role resolved from JWT (e.g. 'admin' | 'user'). null until loaded. */
  userRole: string | null;
}

export interface AuthContextValue extends AuthState {
  requestTokenRefresh: () => void;
  /** Open Google sign-in popup. */
  signInWithGoogle?: () => void;
  /** Sign out from Google session. */
  signOutFromGoogle?: () => void;
  /** Return the current Gorgias Apps Token (for dev tooling). */
  getAppsToken?: () => string;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

/** Returns the embed auth context, or null when not inside a provider. */
export function useOptionalAuth(): AuthContextValue | null {
  return useContext(AuthContext);
}
